import React from 'react';
import { Box, HStack, VStack, Icon, Heading, Link, List, ListItem } from '@chakra-ui/react';
import { FaFileContract } from 'react-icons/fa';
import { IContrat, IFaturas } from '@/service/club/types';
import { getContractDisplayFields } from '@/utils/contractDisplay';
import { formatDateBR } from '@/utils/date';
import { Label } from '@/components/atoms/Label';

interface FaturaDetailCardProps {
  lastFatura?: IFaturas;
  lastContrato?: IContrat;
  colors: { primaryColor: string };
  profileName?: string;
  onBoletoClick?: (url: string) => void;
}

export function FaturaDetailCard({
  lastFatura,
  lastContrato,
  colors,
  profileName,
  onBoletoClick,
}: FaturaDetailCardProps) {
  if (!lastContrato && !lastFatura) return null;

  return (
    <Box
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      backgroundColor="white"
      width="100%"
      minHeight="200px"
      position="relative"
      overflow="hidden"
      transition="all 0.2s ease"
      _hover={{
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        borderColor: 'gray.300',
        transform: 'translateY(-2px)',
      }}
      _before={{
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        backgroundColor: colors.primaryColor,
        borderRadius: '4px 0 0 4px',
      }}
    >
      {lastFatura && (
        <Box
          position="absolute"
          bottom="12px"
          right="12px"
          backgroundColor={lastFatura.fatura_paga ? '#10B981' : '#F59E0B'}
          color="white"
          px="8px"
          py="4px"
          borderRadius="full"
          fontSize="12px"
          fontWeight="600"
          textTransform="uppercase"
          letterSpacing="0.5px"
          boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
        >
          {lastFatura.fatura_paga ? 'Pago' : 'Pendente'}
        </Box>
      )}

      <VStack spacing={3} align="stretch" width="100%" p={4}>
        <HStack spacing={3} align="flex-start">
          <Box
            p={1.5}
            borderRadius="md"
            backgroundColor={`${colors.primaryColor}12`}
            color={colors.primaryColor}
            flexShrink={0}
          >
            <Icon as={FaFileContract} boxSize={4} />
          </Box>
          <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
            <Label fontSize="lg" fontWeight="700" color={colors.primaryColor}>
              {profileName}
            </Label>
            {lastContrato &&
              (() => {
                const fields = getContractDisplayFields(lastContrato);
                const numero = fields.find((f) => f.key === 'numero_contrato' || f.key === 'codigo_contrato');
                const nomeContrato = fields.find((f) => f.key === 'nome_contrato');
                const servico = fields.find((f) => f.key === 'nome_servico');
                const parts: React.ReactNode[] = [];
                if (numero) {
                  parts.push(
                    <>
                      <span style={{ color: '#64748b', fontWeight: 400 }}>{numero.label}: </span>
                      <span style={{ fontWeight: 600 }}>{numero.value}</span>
                    </>
                  );
                }
                if (nomeContrato) {
                  if (parts.length) parts.push(' · ');
                  parts.push(
                    <>
                      <span style={{ color: '#64748b', fontWeight: 400 }}>{nomeContrato.label}: </span>
                      <span style={{ fontWeight: 600 }}>{nomeContrato.value}</span>
                    </>
                  );
                }
                if (servico) {
                  if (parts.length) parts.push(' · ');
                  parts.push(
                    <>
                      <span style={{ color: '#64748b', fontWeight: 400 }}>{servico.label}: </span>
                      <span style={{ fontWeight: 600 }}>{servico.value}</span>
                    </>
                  );
                }
                return (
                  <Label fontSize="sm" color="gray.600" fontWeight="500" noOfLines={3} lineHeight="1.3">
                    {parts}
                  </Label>
                );
              })()}
          </VStack>
        </HStack>

        {lastFatura ? (
          <Box
            py={2.5}
            px={3}
            borderRadius="lg"
            backgroundColor="gray.50"
            border="1px solid"
            borderColor="gray.100"
          >
            <Label fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" letterSpacing="wider" mb={1}>
              Valor da Fatura
            </Label>
            <Heading size="md" color={colors.primaryColor} fontWeight="700">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(lastFatura?.valor))}
            </Heading>
          </Box>
        ) : (
          lastContrato &&
          (() => {
            const valorField = getContractDisplayFields(lastContrato).find((f) => f.key === 'valor_servico');
            return valorField ? (
              <Box
                py={2.5}
                px={3}
                borderRadius="lg"
                backgroundColor="gray.50"
                border="1px solid"
                borderColor="gray.100"
              >
                <Label fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" letterSpacing="wider" mb={1}>
                  {valorField.label}
                </Label>
                <Heading size="md" color={colors.primaryColor} fontWeight="700">
                  {valorField.value}
                </Heading>
              </Box>
            ) : null;
          })()
        )}

        {lastContrato &&
          (() => {
            const detailFields = getContractDisplayFields(lastContrato).filter(
              (f) =>
                !['numero_contrato', 'codigo_contrato', 'nome_servico', 'nome_contrato', 'valor_servico'].includes(
                  f.key
                )
            );
            const contratoLink = lastContrato.link;
            const contratoProdutos = lastContrato.produtos ?? [];
            return (
              <>
                {(detailFields.length > 0 || contratoLink) && (
                  <Box pt={2} borderTop="1px solid" borderColor="gray.100">
                    <VStack align="stretch" spacing={1.5}>
                      {detailFields.map(({ key, label, value }) => (
                        <HStack key={key} justify="space-between" align="flex-start" spacing={2}>
                          <Label fontSize="xs" color="gray.500" fontWeight="500" flexShrink={0}>
                            {label}:
                          </Label>
                          <Label fontSize="sm" color="gray.800" fontWeight="600" textAlign="right" noOfLines={2}>
                            {value}
                          </Label>
                        </HStack>
                      ))}
                      {contratoLink && onBoletoClick && (
                        <HStack justify="space-between" align="flex-start" spacing={2}>
                          <Label fontSize="xs" color="gray.500" fontWeight="500" flexShrink={0}>
                            Contrato:
                          </Label>
                          <Link
                            fontSize="sm"
                            fontWeight="600"
                            color={colors.primaryColor}
                            textDecoration="underline"
                            textAlign="right"
                            _hover={{ textDecoration: 'underline', opacity: 0.8 }}
                            onClick={(e) => {
                              e.preventDefault();
                              onBoletoClick(contratoLink);
                            }}
                          >
                            Visualizar Contrato
                          </Link>
                        </HStack>
                      )}
                    </VStack>
                  </Box>
                )}
                {contratoProdutos.length > 0 && (
                  <Box width="100%">
                    <Label fontSize="xs" color="gray.500" fontWeight="600" mb={1} display="block">
                      Produtos
                    </Label>
                    <List spacing={0} fontSize="sm" color="gray.700">
                      {contratoProdutos.map((item, i) => (
                        <ListItem key={i} display="flex" alignItems="flex-start" gap={2}>
                          <Box as="span" color={colors.primaryColor} mt="3px" flexShrink={0}>
                            •
                          </Box>
                          <Label fontSize="xs" color="gray.700">
                            {item}
                          </Label>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </>
            );
          })()}

        {lastFatura && (
          <>
            {lastFatura.descricao && (
              <Label
                textTransform="capitalize"
                color="gray.800"
                fontWeight="600"
                textAlign="left"
                fontSize="sm"
                lineHeight="1.3"
                noOfLines={2}
              >
                {lastFatura.descricao}
              </Label>
            )}
            <Label color="gray.600" fontWeight="500" textAlign="left" fontSize="sm" lineHeight="1.3">
              Vence dia:{' '}
              <span style={{ fontWeight: 600 }}>{formatDateBR(lastFatura.vencimento, "dd 'de' MMMM 'de' yyyy")}</span>
            </Label>
          </>
        )}
      </VStack>

      {lastFatura && (
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          height="4px"
          backgroundColor={lastFatura.fatura_paga ? '#10B981' : '#F59E0B'}
        />
      )}
    </Box>
  );
}
